import type { Address } from 'app-components/flux/addresses/addressTypes';
import type { BusinessColor } from 'app-components/utils/theme';
import { Record } from 'immutable';
import type { RecordOf } from 'immutable';
import type { QcsSyncResourceStore } from '../core/resourceStoreTypes';

type AccountingType = 'CASH' | 'ACCRUAL';

export type BusinessProps = {
  id?: string;
  clientId?: string;
  isClosed?: boolean;
  createdAt?: string;
  name?: string;
  nickname?: string;
  address?: Address;
  type?: string;
  description?: string;
  tagId?: string;
  isDefault?: boolean;
  isDeleted?: boolean;
  color?: BusinessColor;
  structure?: string;
  taxGroup?: string;
  industry?: string;
  creating?: boolean;
  email?: string;
  phoneNumber?: string;
  nextInvoiceNo?: number;
  logoId?: string;
  accountingType?: AccountingType;
};
export type Business = RecordOf<BusinessProps>;

export const mkBusiness = Record<BusinessProps>({
  id: undefined,
  clientId: undefined,
  createdAt: undefined,
  name: undefined,
  industry: undefined,
  structure: undefined,
  color: undefined,
  nickname: undefined,
  address: undefined,
  type: undefined,
  description: undefined,
  tagId: undefined,
  taxGroup: undefined,
  creating: undefined,
  isDefault: undefined,
  isDeleted: undefined,
  isClosed: undefined,
  email: undefined,
  phoneNumber: undefined,
  nextInvoiceNo: undefined,
  logoId: undefined,
  accountingType: undefined,
});

export const industryObj = {
  NOT_SPECIFIED: 'Not specified',
  CONSULTING_CONTRACTING: 'Consulting/contracting',
  RENTAL_PROPERTY: 'Rental property',
  COMMERCIAL_PROPERTY: 'Commercial property',
  LEGAL: 'Legal',
  FOOD_SERVICES: 'Food services',
  RETAIL: 'Retail',
  ONLINE_SELLING: 'Online selling',
  DRIVING_DELIVERY: 'Driving/delivery',
  MAINTENANCE_REPAIR: 'Maintenance/repair',
  SUPPORT_SERVICES: 'Support services',
  REAL_ESTATE_AGENT: 'Real estate',
  FARMING_AGRICULTURE: 'Agriculture',
  BEAUTY_PERSONAL_CARE: 'Beauty/personal care',
} as const;

export const businessStructureObj = {
  SOLE_PROPRIETOR: 'Sole Proprietor',
  SINGLE_MEMBER_LLC: 'LLC, single member',
  MULTI_MEMBER_LLC: 'LLC, multi member',
  PARTNERSHIP: 'Partnership',
  S_CORP: 'S Corp',
  C_CORP: 'C Corp',
} as const;

export const taxGroupNames = {
  BUSINESS: 'Schedule C',
  BUSINESS_S_CORP: 'Form 1120-S',
  FARMING: 'Schedule F',
  RENTAL_PROPERTY: 'Schedule E',
  PARTNERSHIP: 'Form 1065',
} as const;

export const taxGroupCategoryNames = {
  BUSINESS: 'General Business',
  BUSINESS_S_CORP: 'S Corp',
  FARMING: 'Farming',
  RENTAL_PROPERTY: 'Rental Property',
  PARTNERSHIP: 'Partnership',
} as const;

export const usStatesArray = [
  'AL',
  'AK',
  'AS',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'DC',
  'FM',
  'FL',
  'GA',
  'GU',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MH',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'MP',
  'OH',
  'OK',
  'OR',
  'PW',
  'PA',
  'PR',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VI',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
] as const;

export const BUSINESS_REDUCER_KEY = 'businessStore';
export type BUSINESS_REDUCER_KEY = typeof BUSINESS_REDUCER_KEY;
export const BUSINESS_VERSION_NUMBER = 4;

declare module 'app-components/utils/redux-store/rootState' {
  export default interface RootState {
    [BUSINESS_REDUCER_KEY]: QcsSyncResourceStore<BusinessProps>;
  }
}
